<template>
    <div>
        <label v-if="label" :for="toggleId" class="d-block">{{ label }}:</label>
        <b-dropdown ref="dropdown" :id="dropdownId" variant="outline-primary" block lazy
            class="queue-multiselect" :disabled="disabled" @shown="onShown"
            :toggle-attrs="{ 'aria-label': accessibleLabel }">
            <template #button-content>
                <span class="queue-multiselect-label" :title="dropdownLabel">{{ dropdownLabel }}</span>
            </template>
            <b-dropdown-form class="sticky-top bg-white" style="top: -10px;"
                form-class="queue-multiselect-search-form">
                <b-form-input ref="search" v-model="searchText" :placeholder="searchPlaceholder" size="sm"
                    class="glass-pane"
                    @keydown.down.prevent="highlightNext"
                    @keydown.up.prevent="highlightPrev"
                    @keydown.enter.prevent="selectHighlighted" />
            </b-dropdown-form>
            <b-dropdown-text v-if="visibleOptions.length === 0" class="text-muted small">
                {{ emptyText }}
            </b-dropdown-text>
            <b-dropdown-form v-for="(option, index) in visibleOptions" :key="option.key"
                :class="['py-1', { 'queue-multiselect-highlighted': index === highlightedIndex }]">
                <div class="d-flex align-items-center">
                    <b-form-checkbox :checked="isSelected(option)" @change="toggle(option)" class="mr-2" />
                    <span @click.stop="selectOnly(option)" class="flex-grow-1"
                        :style="{ cursor: 'pointer', color: isDimmed(option) ? '#adb5bd' : undefined }">
                        {{ option.name }}
                    </span>
                    <b-badge v-if="counts[option.key]" variant="primary" class="ml-1">
                        {{ counts[option.key] }}
                    </b-badge>
                </div>
            </b-dropdown-form>
            <b-dropdown-divider />
            <b-dropdown-item v-if="optionsAreComplete && value.length < options.length" @click="selectAll">
                Select All
            </b-dropdown-item>
            <b-dropdown-item v-if="value.length > 0" @click="clear">
                Clear Selection
            </b-dropdown-item>
        </b-dropdown>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

export interface QueueFilterOption {
    /** Stable identity for the option. Composite keys are rendered as strings (e.g. "0:4412"). */
    key: string | number;
    name: string;
}

let instanceCount = 0;

/**
 * Excel-style value filter for a queue column: a searchable checkbox list with per-value counts,
 * "select only this one" on the label, and Select All / Clear Selection.
 *
 * v-models an array of option keys rather than the option objects themselves, so callers whose
 * options are rebuilt from the server (counts endpoints) don't lose their selection on refresh.
 */
@Component
export default class QueueMultiSelectFilter extends Vue {
    /** Own id so the visible <label> has something to point `for` at. */
    private readonly dropdownId = `queue-multiselect-${++instanceCount}`;
    @Prop({ default: "" }) readonly label!: string;
    @Prop({ required: true }) readonly options!: QueueFilterOption[];
    @Prop({ default: () => [] }) readonly value!: Array<string | number>;
    /** Optional per-key counts. When empty, no option is dimmed. */
    @Prop({ default: () => ({}) }) readonly counts!: Record<string | number, number>;
    @Prop({ default: "All" }) readonly allLabel!: string;
    @Prop({ default: "Search..." }) readonly searchPlaceholder!: string;
    @Prop({ default: "No matches" }) readonly emptyText!: string;
    @Prop({ default: false }) readonly disabled!: boolean;
    /**
     * True when `options` is the whole domain (a lookup table), false when it is a scoped query
     * result. Select All over a scoped list would pin the filter to today's result set and silently
     * exclude values that become eligible later, so it is hidden in that case.
     */
    @Prop({ default: true }) readonly optionsAreComplete!: boolean;

    private searchText = "";
    private highlightedIndex = 0;

    /** BootstrapVue's `safeId(suffix)` renders as `id + '_' + suffix`, and the toggle uses `_BV_toggle_`. */
    get toggleId(): string {
        return `${this.dropdownId}__BV_toggle_`;
    }

    /**
     * The toggle's own text is the current value alone ("All Programs"), which names the selection
     * but not the field it belongs to. Spelling out both keeps the field name in the announcement
     * without duplicating it visually -- and, unlike a `for` association, does not depend on
     * BootstrapVue's internal id suffix staying put.
     */
    get accessibleLabel(): string {
        return this.label ? `${this.label}: ${this.dropdownLabel}` : this.dropdownLabel;
    }

    get visibleOptions(): QueueFilterOption[] {
        if (!this.searchText) return this.options;
        const search = this.searchText.toLowerCase();
        return this.options.filter(o => (o.name ?? "").toLowerCase().includes(search));
    }

    private get selectedKeys(): Set<string | number> {
        return new Set(this.value);
    }

    get dropdownLabel(): string {
        if (this.value.length === 0) return this.allLabel;
        const names = this.options
            .filter(o => this.selectedKeys.has(o.key))
            .map(o => o.name)
            .sort();
        // Names are unavailable until the option list loads, and unreadable past a handful.
        return names.length && names.length === this.value.length && names.length <= 5
            ? names.join(", ")
            : `${this.value.length} selected`;
    }

    private get hasCounts(): boolean {
        return Object.keys(this.counts).length > 0;
    }

    isSelected(option: QueueFilterOption): boolean {
        return this.selectedKeys.has(option.key);
    }

    /** Dim options with nothing behind them, but only once counts have actually loaded. */
    isDimmed(option: QueueFilterOption): boolean {
        return this.hasCounts && !this.counts[option.key];
    }

    onShown() {
        this.searchText = "";
        this.highlightedIndex = 0;
        setTimeout(() => {
            (this.$refs.search as any)?.focus();
        }, 50);
    }

    @Watch("searchText")
    onSearchChanged() {
        this.highlightedIndex = 0;
    }

    highlightNext() {
        if (this.highlightedIndex < this.visibleOptions.length - 1) {
            this.highlightedIndex++;
        }
    }

    highlightPrev() {
        if (this.highlightedIndex > 0) {
            this.highlightedIndex--;
        }
    }

    selectHighlighted() {
        const option = this.visibleOptions[this.highlightedIndex];
        if (option) this.toggle(option);
    }

    toggle(option: QueueFilterOption) {
        const next = this.selectedKeys.has(option.key)
            ? this.value.filter(k => k !== option.key)
            : [...this.value, option.key];
        this.$emit("input", next);
    }

    selectOnly(option: QueueFilterOption) {
        const alreadyOnlyThis = this.value.length === 1 && this.value[0] === option.key;
        this.$emit("input", alreadyOnlyThis ? [] : [option.key]);
        (this.$refs.dropdown as any)?.hide();
    }

    selectAll() {
        this.$emit("input", this.options.map(o => o.key));
    }

    clear() {
        this.$emit("input", []);
    }
}
</script>

<style scoped>
/* min-width lets the control shrink inside its column; without it the nowrap label sets a
   min-content floor and the column widens to fit the selection instead of truncating it. */
.queue-multiselect {
    min-width: 0;
    max-width: 100%;
}

/* Flex keeps the caret pinned right so only the label ellipsizes -- the caret is an inline
   ::after, so truncating the toggle's text directly would swallow it. Zeroing the theme's
   global .btn margin lines the toggle up with the sibling SearchComponent input. */
.queue-multiselect ::v-deep(.dropdown-toggle) {
    display: flex;
    align-items: center;
    height: calc(1.5em + 0.75rem + 2px);
    margin: 0;
    max-width: 100%;
    overflow: hidden;
}

.queue-multiselect-label {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
}

.queue-multiselect ::v-deep(.dropdown-menu) {
    max-height: 500px;
    overflow-y: auto;
}

.queue-multiselect ::v-deep(.b-dropdown-form:not(.queue-multiselect-search-form):hover),
.queue-multiselect ::v-deep(.queue-multiselect-highlighted > .b-dropdown-form) {
    background-color: #f8f9fa;
}
</style>
